<script setup>
import { ref, computed, watch, onMounted, shallowRef } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import {
    message,
    Button,
    Card,
    Tag,
    Switch,
    Spin,
    DatePicker
} from 'ant-design-vue';
import {
    ArrowLeftOutlined,
    LeftOutlined,
    RightOutlined
} from '@ant-design/icons-vue';
import dayjs from 'dayjs';
import {
    getConsultantScheduleSlots,
    getConsultantScheduleRules,
    updateConsultantScheduleRules,
    getConsultants
} from '@/api/consultation/consultationApi';
import { usePermission } from '@/composables/usePermission';
import { CONSULTATION_SLOT_DEFS } from '@/constants/consultantScheduleSlots';
import {
    mapSlotsToRowsForDate,
    applyOverridesToRows,
    summarizeMonth,
    buildMonthRulesFromState,
    mergeRulesForPut,
    isWeekendBlackoutDay
} from '@/utils/consultantScheduleMap';
import ScheduleMinSlotsNotice from '@/components/consultation/ScheduleMinSlotsNotice.vue';

const route = useRoute();
const router = useRouter();
const { canView, canUpdate } = usePermission('ConsultantScheduleEdit');

const coreMemberId = computed(() => String(route.params.coreMemberId || ''));
const selectedMonth = ref(
    String(route.query.month || dayjs().format('YYYY-MM'))
);

const loading = ref(false);
const saving = ref(false);
const consultantName = ref('');
const slotsRaw = ref([]);
const bookingsByKey = ref({});
const allRulesSnapshot = ref([]);
const minMonthlyOpenSlots = ref(0);
const levelName = ref('');
const selectedDate = ref('');
const slotOverrides = shallowRef(new Map());

const WEEKDAY_HEADERS = ['日', '一', '二', '三', '四', '五', '六'];

const monthLabel = computed(() =>
    dayjs(`${selectedMonth.value}-01`).format('YYYY年M月')
);

const monthBounds = computed(() => {
    const start = dayjs(`${selectedMonth.value}-01`).startOf('month');
    return { start, end: start.endOf('month') };
});

const isMonthPast = computed(() =>
    monthBounds.value.end.endOf('day').isBefore(dayjs(), 'day')
);

const monthEditable = computed(() => !isMonthPast.value);

const summaries = computed(() =>
    summarizeMonth(
        slotsRaw.value,
        slotOverrides.value,
        monthBounds.value.start,
        monthBounds.value.end,
        bookingsByKey.value
    )
);

const monthOpenCount = computed(() =>
    Object.values(summaries.value).reduce((sum, s) => sum + (s?.open ?? 0), 0)
);

const calendarCells = computed(() => {
    const start = monthBounds.value.start;
    const daysInMonth = start.daysInMonth();
    const leading = start.day();
    const cells = [];
    const today = dayjs().format('YYYY-MM-DD');

    for (let i = 0; i < leading; i++) {
        cells.push({ empty: true, key: `pad-${i}` });
    }
    for (let d = 1; d <= daysInMonth; d++) {
        const date = start.date(d).format('YYYY-MM-DD');
        const summary = summaries.value[date] ?? {
            open: 0,
            booked: 0,
            blocked: false
        };
        const day = start.date(d);
        cells.push({
            empty: false,
            key: date,
            date,
            day: d,
            isToday: date === today,
            summary,
            blocked: summary.blocked || isWeekendBlackoutDay(day)
        });
    }
    return cells;
});

const dayRows = computed(() => {
    if (!selectedDate.value) return [];
    const baseline = mapSlotsToRowsForDate(
        slotsRaw.value,
        selectedDate.value,
        bookingsByKey.value
    );
    return applyOverridesToRows(
        baseline,
        selectedDate.value,
        slotOverrides.value
    );
});

const selectedDateLabel = computed(() => {
    if (!selectedDate.value) return '';
    const d = dayjs(selectedDate.value);
    const w = ['週日', '週一', '週二', '週三', '週四', '週五', '週六'][d.day()];
    return `${d.format('M月D日')}（${w}）`;
});

const dirtyCount = computed(() => {
    let n = 0;
    const { start, end } = monthBounds.value;
    let cursor = start;
    while (cursor.isBefore(end) || cursor.isSame(end, 'day')) {
        const dk = cursor.format('YYYY-MM-DD');
        const baseline = mapSlotsToRowsForDate(
            slotsRaw.value,
            dk,
            bookingsByKey.value
        );
        const inner = slotOverrides.value.get(dk);
        for (const row of baseline) {
            if (row.state !== 'on' && row.state !== 'off') continue;
            const baseOpen = row.state === 'on';
            const eff = inner?.has(row.time)
                ? Boolean(inner.get(row.time))
                : baseOpen;
            if (eff !== baseOpen) n++;
        }
        cursor = cursor.add(1, 'day');
    }
    return n;
});

const pickFirstEditableDay = () => {
    const { start, end } = monthBounds.value;
    let cursor = start;
    const today = dayjs().startOf('day');
    while (cursor.isBefore(end) || cursor.isSame(end, 'day')) {
        if (!cursor.isBefore(today, 'day') && !isWeekendBlackoutDay(cursor)) {
            return cursor.format('YYYY-MM-DD');
        }
        cursor = cursor.add(1, 'day');
    }
    return start.format('YYYY-MM-DD');
};

const loadConsultantName = async () => {
    const res = await getConsultants();
    if (res.status !== 1) return;
    const row = (res.data || []).find((c) => c.core_member_id === coreMemberId.value);
    consultantName.value = row?.display_name || coreMemberId.value;
};

const loadRulesSnapshot = async () => {
    const res = await getConsultantScheduleRules(coreMemberId.value, {
        month: selectedMonth.value
    });
    if (res.status !== 1) return;
    const raw = res.data?.rules ?? res.data ?? [];
    allRulesSnapshot.value = Array.isArray(raw) ? raw : [];
    minMonthlyOpenSlots.value = Number(res.data?.min_monthly_open_slots) || 0;
    levelName.value = res.data?.level_name || '';
};

const load = async () => {
    if (!coreMemberId.value || !canView.value) return;
    loading.value = true;
    slotOverrides.value = new Map();
    try {
        const { start, end } = monthBounds.value;
        const [slotsRes] = await Promise.all([
            getConsultantScheduleSlots(coreMemberId.value, {
                from: start.format('YYYY-MM-DD'),
                to: end.format('YYYY-MM-DD')
            }),
            loadRulesSnapshot()
        ]);
        if (slotsRes.status !== 1) throw new Error(slotsRes.message || '載入失敗');
        slotsRaw.value = slotsRes.data?.slots ?? [];
        bookingsByKey.value = slotsRes.data?.bookings ?? {};
        selectedDate.value = pickFirstEditableDay();
    } catch (e) {
        message.error(e.message || '載入班表失敗');
    } finally {
        loading.value = false;
    }
};

const selectDate = (cell) => {
    if (cell.empty || cell.blocked) return;
    if (dayjs(cell.date).isBefore(dayjs(), 'day')) return;
    selectedDate.value = cell.date;
};

const isRowTogglable = (row) => {
    if (!monthEditable.value || !selectedDate.value) return false;
    if (dayjs(selectedDate.value).isBefore(dayjs(), 'day')) return false;
    return row.state === 'on' || row.state === 'off';
};

const setOverride = (time, open) => {
    const dk = selectedDate.value;
    const baseline = mapSlotsToRowsForDate(
        slotsRaw.value,
        dk,
        bookingsByKey.value
    );
    const baseRow = baseline.find((r) => r.time === time);
    if (!baseRow || (baseRow.state !== 'on' && baseRow.state !== 'off')) return;

    const baseOpen = baseRow.state === 'on';
    const next = new Map(slotOverrides.value);
    const inner = new Map(next.get(dk) ?? []);
    if (open === baseOpen) inner.delete(time);
    else inner.set(time, open);
    if (inner.size === 0) next.delete(dk);
    else next.set(dk, inner);
    slotOverrides.value = next;
};

const openAllDay = () => {
    dayRows.value.forEach((row) => {
        if (isRowTogglable(row)) setOverride(row.time, true);
    });
};

const closeAllDay = () => {
    dayRows.value.forEach((row) => {
        if (isRowTogglable(row)) setOverride(row.time, false);
    });
};

const shiftMonth = (delta) => {
    if (dirtyCount.value > 0) {
        message.warning('請先儲存或還原變更後再切換月份');
        return;
    }
    selectedMonth.value = dayjs(`${selectedMonth.value}-01`)
        .add(delta, 'month')
        .format('YYYY-MM');
};

const save = async () => {
    if (!canUpdate.value) return;
    saving.value = true;
    try {
        const displayMonth = monthBounds.value.start;
        const newMonthRules = buildMonthRulesFromState(
            displayMonth,
            slotsRaw.value,
            slotOverrides.value
        );
        const merged = mergeRulesForPut(
            allRulesSnapshot.value,
            monthBounds.value.start,
            monthBounds.value.end,
            newMonthRules
        );
        const res = await updateConsultantScheduleRules(coreMemberId.value, {
            service_type: 'consultation',
            month: selectedMonth.value,
            rules: merged
        });
        if (res.status !== 1) throw new Error(res.message || '儲存失敗');
        message.success('已代排並標記為管理員設定');
        router.push({
            name: 'ConsultantSchedule',
            params: { coreMemberId: coreMemberId.value }
        });
    } catch (e) {
        message.error(e.message || '儲存失敗');
    } finally {
        saving.value = false;
    }
};

const goBack = () => {
    if (dirtyCount.value > 0) {
        message.warning('尚有未儲存的變更');
        return;
    }
    router.push({
        name: 'ConsultantSchedule',
        params: { coreMemberId: coreMemberId.value }
    });
};

watch(selectedMonth, () => {
    router.replace({ query: { month: selectedMonth.value } });
    load();
});

onMounted(async () => {
    await loadConsultantName();
    await load();
});
</script>

<template>
    <div class="schedule-edit pb-10">
        <p v-if="!canView" class="text-slate-500">無檢視權限</p>
        <p v-else-if="!canUpdate" class="text-slate-500">無代排權限</p>

        <template v-else>
            <div class="schedule-edit-page-bar mb-4">
                <Button type="text" class="schedule-edit-page-bar__back" @click="goBack">
                    <template #icon><ArrowLeftOutlined /></template>
                    返回排班檢視
                </Button>
                <h1 class="schedule-edit-page-bar__title">代排班表</h1>
            </div>

            <Spin :spinning="loading">
                <Card
                    :bordered="false"
                    class="admin-panel shadow-md rounded-2xl border border-[#ebe4d6] bg-white"
                >
                    <h2 class="m-0 text-xl font-semibold text-slate-800">
                        {{ consultantName }}
                    </h2>

                    <div class="mt-4 flex flex-wrap items-center gap-3">
                        <DatePicker
                            v-model:value="selectedMonth"
                            picker="month"
                            value-format="YYYY-MM"
                            :allow-clear="false"
                            :disabled="dirtyCount > 0"
                        />
                        <span v-if="dirtyCount > 0" class="text-sm text-amber-700">
                            有 {{ dirtyCount }} 處變更未儲存
                        </span>
                    </div>
                    <ScheduleMinSlotsNotice
                        class="mt-3"
                        :min="minMonthlyOpenSlots"
                        :current="monthOpenCount"
                        :level-name="levelName"
                        variant="edit"
                    />

                    <div class="schedule-edit__layout mt-4">
                        <div class="schedule-edit__calendar">
                            <div class="schedule-edit__cal-head">
                                <Button type="text" size="small" @click="shiftMonth(-1)">
                                    <template #icon><LeftOutlined /></template>
                                </Button>
                                <span class="font-medium">{{ monthLabel }}</span>
                                <Button type="text" size="small" @click="shiftMonth(1)">
                                    <template #icon><RightOutlined /></template>
                                </Button>
                            </div>
                            <div class="schedule-edit__weekdays">
                                <span
                                    v-for="(w, i) in WEEKDAY_HEADERS"
                                    :key="w"
                                    :class="{ 'is-weekend': i === 0 || i === 6 }"
                                >
                                    {{ w }}
                                </span>
                            </div>
                            <div class="schedule-edit__grid">
                                <template v-for="cell in calendarCells" :key="cell.key">
                                    <div
                                        v-if="cell.empty"
                                        class="schedule-edit__cell schedule-edit__cell--empty"
                                    />
                                    <button
                                        v-else
                                        type="button"
                                        class="schedule-edit__cell"
                                        :class="{
                                            'is-selected': selectedDate === cell.date,
                                            'is-today': cell.isToday,
                                            'is-blocked': cell.blocked
                                        }"
                                        :disabled="cell.blocked"
                                        @click="selectDate(cell)"
                                    >
                                        <span class="schedule-edit__day-num">{{
                                            cell.day
                                        }}</span>
                                        <span
                                            v-if="!cell.blocked && cell.summary.open > 0"
                                            class="schedule-edit__pill schedule-edit__pill--open"
                                        >
                                            開 {{ cell.summary.open }}
                                        </span>
                                        <span
                                            v-if="cell.summary.booked > 0"
                                            class="schedule-edit__pill schedule-edit__pill--booked"
                                        >
                                            約 {{ cell.summary.booked }}
                                        </span>
                                    </button>
                                </template>
                            </div>
                        </div>

                        <aside class="schedule-edit__panel">
                            <p class="schedule-edit__panel-title">
                                {{ selectedDateLabel || '請選擇日期' }}
                            </p>
                            <div
                                v-if="selectedDate && monthEditable"
                                class="schedule-edit__day-actions"
                            >
                                <button
                                    type="button"
                                    class="schedule-edit__day-btn schedule-edit__day-btn--open"
                                    @click="openAllDay"
                                >
                                    本日全開
                                </button>
                                <button
                                    type="button"
                                    class="schedule-edit__day-btn schedule-edit__day-btn--close"
                                    @click="closeAllDay"
                                >
                                    本日全關
                                </button>
                            </div>
                            <ul v-if="selectedDate" class="schedule-edit__slots">
                                <li
                                    v-for="row in dayRows"
                                    :key="row.time"
                                    class="schedule-edit__slot-row"
                                >
                                    <span class="schedule-edit__slot-time">
                                        {{
                                            CONSULTATION_SLOT_DEFS.find(
                                                (d) => d.start === row.time
                                            )?.label || row.time
                                        }}
                                    </span>
                                    <span class="schedule-edit__slot-meta">
                                        <Tag
                                            v-if="row.adminScheduled"
                                            color="orange"
                                            class="!m-0 mr-1 min-w-[1.25rem] !px-1 text-center !text-[11px]"
                                        >
                                            代
                                        </Tag>
                                        <template v-if="row.state === 'locked'">
                                            <span class="block">{{ row.meta || '已預約' }}</span>
                                            <span
                                                v-if="row.versionName"
                                                class="block text-[10px] text-slate-500"
                                            >
                                                {{ row.versionName }}
                                            </span>
                                        </template>
                                        <template v-else-if="row.state === 'policy'">
                                            不開放
                                        </template>
                                        <template v-else-if="row.state === 'disabled'">
                                            不可排
                                        </template>
                                        <template v-else>
                                            {{ row.state === 'on' ? '開放' : '關閉' }}
                                        </template>
                                    </span>
                                    <Switch
                                        v-if="isRowTogglable(row)"
                                        :checked="row.state === 'on'"
                                        :disabled="!monthEditable"
                                        @change="(checked) => setOverride(row.time, checked)"
                                    />
                                    <Switch
                                        v-else-if="row.state === 'locked'"
                                        checked
                                        disabled
                                    />
                                </li>
                            </ul>
                        </aside>
                    </div>

                    <div class="mt-6 flex justify-end gap-2">
                        <Button @click="goBack">取消</Button>
                        <Button
                            type="primary"
                            :loading="saving"
                            :disabled="!monthEditable || dirtyCount === 0"
                            @click="save"
                        >
                            儲存代排
                        </Button>
                    </div>
                </Card>
            </Spin>
        </template>
    </div>
</template>

<style scoped>
.schedule-edit {
    max-width: 1100px;
}

.schedule-edit-page-bar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px 16px;
}

.schedule-edit-page-bar__back.ant-btn {
    padding-inline: 6px;
    color: #6b5428;
    font-weight: 600;
}

.schedule-edit-page-bar__back.ant-btn:hover {
    color: #8b6914;
    background: #faf6ee !important;
}

.schedule-edit-page-bar__title {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 700;
    color: #1f2937;
    letter-spacing: 0.01em;
}

.schedule-edit__layout {
    display: grid;
    gap: 1.25rem;
}

@media (min-width: 900px) {
    .schedule-edit__layout {
        grid-template-columns: 1fr 340px;
    }
}

.schedule-edit__cal-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0.5rem;
}

.schedule-edit__weekdays {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 4px;
    text-align: center;
    font-size: 12px;
    font-weight: 600;
    color: #64748b;
    margin-bottom: 4px;
}

.schedule-edit__weekdays .is-weekend {
    color: #94a3b8;
}

.schedule-edit__grid {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 4px;
}

.schedule-edit__cell {
    min-height: 72px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 4px;
    background: #fff;
    text-align: left;
    cursor: pointer;
}

.schedule-edit__cell--empty {
    border-color: transparent;
    background: transparent;
    cursor: default;
}

.schedule-edit__cell.is-selected {
    border-color: #10b981;
    background: #f6f1e6;
}

.schedule-edit__cell.is-blocked {
    opacity: 0.45;
    cursor: not-allowed;
}

.schedule-edit__day-num {
    display: block;
    font-size: 13px;
    font-weight: 600;
}

.schedule-edit__pill {
    display: block;
    font-size: 9px;
    margin-top: 2px;
}

.schedule-edit__pill--open {
    color: #047857;
}

.schedule-edit__pill--booked {
    color: #1d4ed8;
}

.schedule-edit__panel {
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 1rem;
    background: #fafafa;
    min-height: 280px;
}

.schedule-edit__panel-title {
    margin: 0 0 0.75rem;
    font-weight: 600;
    color: #1e293b;
}

.schedule-edit__day-actions {
    display: flex;
    gap: 8px;
    margin-bottom: 12px;
}

.schedule-edit__day-btn {
    flex: 1;
    min-height: 34px;
    padding: 6px 10px;
    border-radius: 10px;
    border: 1px solid transparent;
    font-size: 13px;
    font-weight: 650;
    letter-spacing: 0.02em;
    cursor: pointer;
    transition:
        background 0.15s ease,
        border-color 0.15s ease,
        color 0.15s ease,
        box-shadow 0.15s ease,
        transform 0.15s ease;
}

.schedule-edit__day-btn--open {
    color: #047857;
    background: #ecfdf5;
    border-color: #a7f3d0;
}

.schedule-edit__day-btn--open:hover {
    color: #065f46;
    background: #d1fae5;
    border-color: #6ee7b7;
    box-shadow: 0 2px 8px rgba(16, 185, 129, 0.18);
}

.schedule-edit__day-btn--close {
    color: #64748b;
    background: #fff;
    border-color: #e2e8f0;
}

.schedule-edit__day-btn--close:hover {
    color: #b91c1c;
    background: #fef2f2;
    border-color: #fecaca;
    box-shadow: 0 2px 8px rgba(185, 28, 28, 0.1);
}

.schedule-edit__day-btn:active {
    transform: translateY(1px);
}

.schedule-edit__slots {
    list-style: none;
    margin: 0;
    padding: 0;
}

.schedule-edit__slot-row {
    display: grid;
    grid-template-columns: 1fr auto auto;
    gap: 8px;
    align-items: center;
    padding: 10px 0;
    border-bottom: 1px solid #e2e8f0;
}

.schedule-edit__slot-row:last-child {
    border-bottom: none;
}

.schedule-edit__slot-time {
    font-size: 13px;
    font-weight: 500;
}

.schedule-edit__slot-meta {
    display: inline-flex;
    align-items: center;
    justify-content: flex-end;
    gap: 4px;
    font-size: 11px;
    color: #64748b;
    max-width: 6.5rem;
    text-align: right;
}
</style>
